import { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { PageHeader } from '@/dashboard/ui';
import {
  advanceLeadStageBackHalf,
  markTreatmentAccepted,
  markTreatmentAdvised,
  markTreatmentDeclined,
} from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  OUTCOME_REASON_OPTIONS,
  outcomeReasonLabel,
} from '@/dashboard/owner/Lead360Panel';
import WalkinFollowupsPanel from '@/walkin/WalkinFollowupsPanel';
import CocFollowupCockpit from '@/dashboard/clinical-workbench/CocFollowupCockpit';
import {
  confirmRevenue,
  OUTCOME_ACTION_LABELS,
  parsePositiveIntId,
  recordOutcome,
  SANDBOX_BANNER_TEXT,
  UNSAVED_CONTEXT_NOTE,
  workbenchViewForRole,
  type BoundAppointment,
  type ClinicalWorkbenchView,
  type OutcomeAction,
  type OutcomeRecordDeps,
  type WorkbenchFeedback,
} from '@/dashboard/clinical-workbench/clinical-workbench-view';

/**
 * AdoraCare - back-half clinical workbench (BACK-HALF-CLINICAL-WORKBENCH-UI-01).
 *
 * The operator-facing screen over the LIVE categorical outcome path. It is a
 * CATEGORICAL clinical operating workbench, NOT a full EHR: it records the
 * consultation OUTCOME (advised / accepted / declined + an optional controlled
 * decline reason) and the positive-conversion stage moves (coc -> patient,
 * owner -> converted_to_revenue) over a FULFILLED appointment / its lead. It
 * writes NO free-text clinical note, NO attachment / x-ray, NO prescription, NO
 * per-consultation cash - none of those have an endpoint.
 *
 * Two role-aware views (mirrors the per-role nav split):
 *   - OUTCOME (coc / doctor): record advised / accepted / declined. "Accepted"
 *     is a UI-orchestrated cascade - POST treatment-accepted THEN POST
 *     /api/leads/:id/stage {patient} - two separate domain events behind one
 *     click; a partial failure (outcome ok, stage fails) is surfaced, not
 *     swallowed.
 *   - REVENUE (owner): confirm the revenue conversion (POST
 *     /api/leads/:id/stage {converted_to_revenue}) + reuse the existing
 *     work-actions follow-up panel for visibility.
 *
 * HARD DATA RULE: there is no fulfilled-appointment LIST read and no
 * single-appointment GET, so the operator identifies the appointment / lead by
 * id; the backend fulfilled-only guard (409 `appointment_not_actionable`) is
 * reflected, never faked into a pre-filter. chief complaint + treatment category
 * have no backend field, so they are captured as on-screen context and NEVER
 * pretended-saved (see clinical-workbench-view.ts). Built to the contract.
 *
 * Tested via react-dom/server (no jsdom): the pure ClinicalWorkbenchContent +
 * the pure helpers in clinical-workbench-view.ts are exercised with controlled
 * props; the default export reads the role from context and wires the async fns.
 */

// --- State + feedback types -------------------------------------------------

export interface OutcomeFormState {
  readonly appointmentId: string;
  readonly chiefComplaint: string;
  readonly treatmentCategory: string;
  /** A controlled OUTCOME_REASONS token, or '' for no reason (optional). */
  readonly declineReason: string;
}

export const EMPTY_OUTCOME_FORM: OutcomeFormState = {
  appointmentId: '',
  chiefComplaint: '',
  treatmentCategory: '',
  declineReason: '',
};

// --- Shared banner ----------------------------------------------------------

function SandboxBanner(): JSX.Element {
  return (
    <div
      className="clinic-workbench__banner"
      role="note"
      data-testid="workbench-banner"
    >
      {SANDBOX_BANNER_TEXT}
    </div>
  );
}

function FeedbackBanner({
  feedback,
}: {
  feedback: WorkbenchFeedback | null;
}): JSX.Element | null {
  if (feedback === null) return null;
  return (
    <div
      className={
        feedback.kind === 'success'
          ? 'workboard__feedback workboard__feedback--success'
          : 'workboard__feedback workboard__feedback--error'
      }
      role="status"
      aria-live="polite"
      data-testid="workbench-feedback"
    >
      <div className="clinic-workbench__feedback-lines">
        {feedback.lines.map((line, i) => (
          <span key={String(i)}>{line}</span>
        ))}
      </div>
    </div>
  );
}

// --- OUTCOME view (coc / doctor) -------------------------------------------

interface OutcomeRecorderProps {
  readonly form: OutcomeFormState;
  readonly bound: BoundAppointment | null;
  readonly busy: OutcomeAction | null;
  readonly feedback: WorkbenchFeedback | null;
  readonly onFormChange: (form: OutcomeFormState) => void;
  readonly onRecord: (action: OutcomeAction) => void;
}

export function OutcomeRecorder({
  form,
  bound,
  busy,
  feedback,
  onFormChange,
  onRecord,
}: OutcomeRecorderProps): JSX.Element {
  const anyBusy = busy !== null;
  return (
    <section className="clinic-table" data-testid="workbench-outcome">
      <h2 className="clinic-table__title">Record consultation outcome</h2>
      <p className="clinic-workbench__hint">
        Record the operational outcome of a <strong>fulfilled</strong>{' '}
        appointment. Find the appointment id in the Lead 360 appointment
        workbench after the visit is marked fulfilled.
      </p>

      <div className="clinic-workbench__field">
        <label htmlFor="workbench-appointment-id">Appointment id</label>
        <input
          id="workbench-appointment-id"
          type="text"
          inputMode="numeric"
          className="clinic-workbench__input"
          placeholder="e.g. 1024"
          value={form.appointmentId}
          disabled={anyBusy}
          onChange={(e) =>
            onFormChange({ ...form, appointmentId: e.target.value })
          }
        />
      </div>

      {bound ? (
        <p className="clinic-workbench__bound" data-testid="workbench-bound">
          Last recorded on appointment #{bound.appointmentId}
          {bound.leadId !== null ? ` (lead #${bound.leadId})` : ' (no linked lead)'}
          {' '}&middot; status {bound.status}
        </p>
      ) : null}

      <div className="clinic-workbench__context">
        <div className="clinic-workbench__field">
          <label htmlFor="workbench-chief-complaint">
            Chief complaint <span className="clinic-workbench__muted">(context)</span>
          </label>
          <input
            id="workbench-chief-complaint"
            type="text"
            maxLength={120}
            className="clinic-workbench__input"
            placeholder="Short categorical complaint"
            value={form.chiefComplaint}
            disabled={anyBusy}
            onChange={(e) =>
              onFormChange({ ...form, chiefComplaint: e.target.value })
            }
          />
        </div>
        <div className="clinic-workbench__field">
          <label htmlFor="workbench-treatment-category">
            Treatment category <span className="clinic-workbench__muted">(context)</span>
          </label>
          <input
            id="workbench-treatment-category"
            type="text"
            maxLength={120}
            className="clinic-workbench__input"
            placeholder="Short category"
            value={form.treatmentCategory}
            disabled={anyBusy}
            onChange={(e) =>
              onFormChange({ ...form, treatmentCategory: e.target.value })
            }
          />
        </div>
      </div>
      <p className="clinic-workbench__note" data-testid="workbench-unsaved-note">
        {UNSAVED_CONTEXT_NOTE}
      </p>

      <div className="clinic-workbench__actions">
        <button
          type="button"
          className="ac-btn ac-btn--primary ac-btn--sm"
          disabled={anyBusy}
          data-testid="workbench-advised"
          onClick={() => onRecord('treatment_advised')}
        >
          {OUTCOME_ACTION_LABELS.treatment_advised}
        </button>
        <button
          type="button"
          className="ac-btn ac-btn--primary ac-btn--sm"
          disabled={anyBusy}
          data-testid="workbench-accepted"
          onClick={() => onRecord('treatment_accepted')}
        >
          {OUTCOME_ACTION_LABELS.treatment_accepted}
        </button>
        <div className="clinic-workbench__decline" role="group" aria-label="Decline">
          <label htmlFor="workbench-decline-reason" className="clinic-workbench__muted">
            Reason (optional)
          </label>
          <select
            id="workbench-decline-reason"
            className="clinic-workbench__input"
            value={form.declineReason}
            disabled={anyBusy}
            onChange={(e) =>
              onFormChange({ ...form, declineReason: e.target.value })
            }
          >
            <option value="">No reason</option>
            {OUTCOME_REASON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            disabled={anyBusy}
            data-testid="workbench-declined"
            onClick={() => onRecord('treatment_declined')}
          >
            {OUTCOME_ACTION_LABELS.treatment_declined}
          </button>
        </div>
      </div>

      <FeedbackBanner feedback={feedback} />
    </section>
  );
}

// --- REVENUE view (owner) ---------------------------------------------------

interface RevenuePanelProps {
  readonly leadId: string;
  readonly busy: boolean;
  readonly feedback: WorkbenchFeedback | null;
  readonly onLeadIdChange: (value: string) => void;
  readonly onConfirmRevenue: () => void;
}

export function RevenuePanel({
  leadId,
  busy,
  feedback,
  onLeadIdChange,
  onConfirmRevenue,
}: RevenuePanelProps): JSX.Element {
  return (
    <>
      <section className="clinic-table" data-testid="workbench-revenue">
        <h2 className="clinic-table__title">Confirm revenue conversion</h2>
        <p className="clinic-workbench__hint">
          Revenue is a separate fact from the clinical outcome. Move a converted
          lead to <strong>converted to revenue</strong> (this closes the case).
        </p>
        <div className="clinic-workbench__field">
          <label htmlFor="workbench-lead-id">Lead id</label>
          <input
            id="workbench-lead-id"
            type="text"
            inputMode="numeric"
            className="clinic-workbench__input"
            placeholder="e.g. 1001"
            value={leadId}
            disabled={busy}
            onChange={(e) => onLeadIdChange(e.target.value)}
          />
        </div>
        <div className="clinic-workbench__actions">
          <button
            type="button"
            className="ac-btn ac-btn--primary ac-btn--sm"
            disabled={busy}
            data-testid="workbench-confirm-revenue"
            onClick={onConfirmRevenue}
          >
            Confirm Revenue Conversion
          </button>
        </div>
        <FeedbackBanner feedback={feedback} />
      </section>

      <section className="clinic-table" data-testid="workbench-followups">
        <h2 className="clinic-table__title">Follow-ups assigned to you</h2>
        <WalkinFollowupsPanel />
      </section>
    </>
  );
}

// --- Pure presentational board ---------------------------------------------

interface ClinicalWorkbenchContentProps {
  // The doctor's 'case_sheet' view is a REDIRECT to the doctor workspace at
  // the page level (DOCTOR-CLINICAL-WORKSPACE-V1), so this pure content only
  // ever renders the coc 'outcome' recorder or the owner 'revenue' panel.
  readonly view: Exclude<ClinicalWorkbenchView, 'case_sheet'>;
  // Outcome view
  readonly form: OutcomeFormState;
  readonly bound: BoundAppointment | null;
  readonly outcomeBusy: OutcomeAction | null;
  readonly outcomeFeedback: WorkbenchFeedback | null;
  readonly onFormChange: (form: OutcomeFormState) => void;
  readonly onRecord: (action: OutcomeAction) => void;
  // Revenue view
  readonly leadId: string;
  readonly revenueBusy: boolean;
  readonly revenueFeedback: WorkbenchFeedback | null;
  readonly onLeadIdChange: (value: string) => void;
  readonly onConfirmRevenue: () => void;
}

export function ClinicalWorkbenchContent({
  view,
  form,
  bound,
  outcomeBusy,
  outcomeFeedback,
  onFormChange,
  onRecord,
  leadId,
  revenueBusy,
  revenueFeedback,
  onLeadIdChange,
  onConfirmRevenue,
}: ClinicalWorkbenchContentProps): JSX.Element {
  return (
    <div className="dashboard clinic-workbench">
      {/* Standard 5 (rule 2 -- one page-header grammar) for the COC station's
          enclosing page. Top-level home: no breadcrumbs (rule 10 N/A). */}
      <PageHeader
        eyebrow="COC station"
        title="Clinical workbench"
        subs={['Categorical consultation outcome - not a full clinical record']}
      />

      <SandboxBanner />

      {view === 'outcome' ? (
        // SANDBOX-CASE-SHEET-COC-COCKPIT-V1 (2C-1): the COC leads with the
        // list-first recovery work queue (the doctor -> COC loop's landing
        // surface); the categorical outcome recorder is PRESERVED below it (the
        // COC may still record an outcome by appointment id). The cockpit is
        // self-contained (its own work-actions read), so this branch just
        // composes the two - no outcome wiring changes.
        <>
          <CocFollowupCockpit />
          <OutcomeRecorder
            form={form}
            bound={bound}
            busy={outcomeBusy}
            feedback={outcomeFeedback}
            onFormChange={onFormChange}
            onRecord={onRecord}
          />
        </>
      ) : (
        <RevenuePanel
          leadId={leadId}
          busy={revenueBusy}
          feedback={revenueFeedback}
          onLeadIdChange={onLeadIdChange}
          onConfirmRevenue={onConfirmRevenue}
        />
      )}
    </div>
  );
}

// --- Stateful wirer (default export) ---------------------------------------

/** The real client fns, injected into the pure orchestrators. */
const OUTCOME_DEPS: OutcomeRecordDeps = {
  markTreatmentAdvised,
  markTreatmentAccepted,
  markTreatmentDeclined,
  advanceLeadStageBackHalf,
};

export default function ClinicalWorkbenchPage(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  const me = ctx?.me ?? null;
  const view: ClinicalWorkbenchView | null = me
    ? workbenchViewForRole(me.role)
    : null;

  // Outcome-view state
  const [form, setForm] = useState<OutcomeFormState>(EMPTY_OUTCOME_FORM);
  const [bound, setBound] = useState<BoundAppointment | null>(null);
  const [outcomeBusy, setOutcomeBusy] = useState<OutcomeAction | null>(null);
  const [outcomeFeedback, setOutcomeFeedback] =
    useState<WorkbenchFeedback | null>(null);

  // Revenue-view state
  const [leadId, setLeadId] = useState<string>('');
  const [revenueBusy, setRevenueBusy] = useState<boolean>(false);
  const [revenueFeedback, setRevenueFeedback] =
    useState<WorkbenchFeedback | null>(null);

  const onRecord = useCallback(
    (action: OutcomeAction) => {
      const apptId = parsePositiveIntId(form.appointmentId);
      if (apptId === null) {
        setOutcomeFeedback({
          kind: 'error',
          lines: ['Enter a valid appointment id (a positive number).'],
        });
        return;
      }
      setOutcomeBusy(action);
      setOutcomeFeedback(null);
      recordOutcome(OUTCOME_DEPS, {
        action,
        appointmentId: apptId,
        declineReason: form.declineReason,
        declineReasonLabel: form.declineReason
          ? outcomeReasonLabel(form.declineReason)
          : '',
        // Only coc owns the `patient` stage (STAGE_CONTRACTS); a doctor records
        // the outcome but does not fire the doomed stage advance.
        advancePatientStage: me?.role === 'coc',
      })
        .then((out) => {
          if (out.bound) setBound(out.bound);
          setOutcomeFeedback(out.feedback);
        })
        .finally(() => setOutcomeBusy(null));
    },
    [form.appointmentId, form.declineReason, me?.role],
  );

  const onConfirmRevenue = useCallback(() => {
    const id = parsePositiveIntId(leadId);
    if (id === null) {
      setRevenueFeedback({
        kind: 'error',
        lines: ['Enter a valid lead id (a positive number).'],
      });
      return;
    }
    setRevenueBusy(true);
    setRevenueFeedback(null);
    confirmRevenue(OUTCOME_DEPS, id)
      .then((fb) => setRevenueFeedback(fb))
      .finally(() => setRevenueBusy(false));
  }, [leadId]);

  // The route guard already redirects a non-workbench role; this defensive
  // null-render covers the brief window before the context resolves.
  if (me === null || view === null) return null;

  // DOCTOR-CLINICAL-WORKSPACE-V1: the doctor's manual-id cockpit is RETIRED
  // (NO-MANUAL-ID LAW; founder finding: #42 loaded when #43 was the day's
  // visit). The doctor branch redirects to the doctor workspace -- consults
  // open from TODAY'S QUEUE (or a ?appointmentId= deep link, honored below
  // as a redirect to the ROUTED consult workspace so old day-board doors
  // never 404 -- the workstation-retirement precedent, App.tsx receptionist
  // route). The coc (outcome recorder by appointment id -- a COC surface,
  // not the doctor surface) and owner (revenue) views are PRESERVED per the
  // visibility doctrine route audit (06-CRM-ROLE-VISIBILITY-DOCTRINE:
  // /dashboard/clinical-workbench stays OWNER-OK with the clinical body
  // carved; rule 4).
  if (view === 'case_sheet') {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const deepLinkId = parsePositiveIntId(
      new URLSearchParams(search).get('appointmentId') ?? '',
    );
    return deepLinkId !== null ? (
      <Navigate to={`/dashboard/doctor/consult/${deepLinkId}`} replace />
    ) : (
      <Navigate to="/dashboard/doctor" replace />
    );
  }

  return (
    <ClinicalWorkbenchContent
      view={view}
      form={form}
      bound={bound}
      outcomeBusy={outcomeBusy}
      outcomeFeedback={outcomeFeedback}
      onFormChange={setForm}
      onRecord={onRecord}
      leadId={leadId}
      revenueBusy={revenueBusy}
      revenueFeedback={revenueFeedback}
      onLeadIdChange={setLeadId}
      onConfirmRevenue={onConfirmRevenue}
    />
  );
}
